import { css } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { EmptyStateNoResults, SearchInput, Pagination, Divider } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentResource, EnvironmentsSummaryResource, Repository, TenantResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { MachineHealthStatus, QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import * as React from "react";
import { Action as AnalyticAction, useAnalyticActionDispatch } from "~/analytics/Analytics";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import endpointRegistry from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { AdvancedFilterCheckbox } from "~/components/AdvancedFilterLayout";
import { AdvancedFilters } from "~/components/AdvancedFilterLayout/AdvancedFilters";
import { AdvancedFilterTextInput } from "~/components/AdvancedFilterLayout/Text/AdvancedFilterTextInput";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { Feature, FeatureToggle } from "~/components/FeatureToggle";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import DeploymentTargetTypeMultiSelect from "~/components/MultiSelect/DeploymentTargetTypeMultiselect";
import { EnvironmentMultiSelect } from "~/components/MultiSelect/EnvironmentMultiSelect";
import { MachineModelHealthStatusMultiSelect } from "~/components/MultiSelect/MachineModelHealthStatusMultiSelect";
import { ShellNameMultiSelect } from "~/components/MultiSelect/ShellNameMultiSelect";
import { TargetTagMultiSelect } from "~/components/MultiSelect/TargetTagMultiSelect";
import { TenantMultiSelect } from "~/components/MultiSelect/TenantMultiSelect";
import { TenantTagMultiSelect } from "~/components/MultiSelect/TenantTagMultiSelect";
import ExternalVideoLink from "~/components/Navigation/ExternalLink/ExternalVideoLink";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import ExpansionButtons from "~/components/form/Sections/ExpansionButtons";
import type { TagIndex } from "~/components/tenantTagsets";
import * as tenantTagsets from "~/components/tenantTagsets";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import PermissionCheck, { isAllowed } from "../../../../components/PermissionCheck/PermissionCheck";
import ConfirmTentacleUpgradePanel from "../MachinesLayout/ConfirmTentacleUpgradePanel";
import { AddEnvironmentsDialog } from "./AddEnvironmentsDialog";
import { createEnvironmentSummaryArgs, defaultEnvironmentSummaryFilter } from "./EnvironmentSummaryFilter";
import EnvironmentSummarySection from "./EnvironmentSummarySection";
import EnvironmentsSorter from "./EnvironmentsSorter";
export interface EnvironmentsPageQueryParams {
    environmentIds: string[];
    roles: string[];
    tenantIds: string[];
    tenantTags: string[];
    shellNames: string[];
    deploymentTargetTypes: string[];
    hideEmptyEnvironments: boolean;
    machinePartialName: string | undefined;
    isDisabled: boolean | undefined;
    healthStatuses: MachineHealthStatus[];
    partialName: string | undefined;
}
interface EnvironmentsPageProps {
    spaceId: string;
    loaderData: EnvironmentsPageLoaderData;
    queryParams: EnvironmentsPageQueryParams;
    setQueryParams: QueryParamValuesSetter<EnvironmentsPageQueryParams>;
}
interface EnvironmentsPageLoaderData {
    environmentsSummary: EnvironmentsSummaryResource;
    environments: EnvironmentResource[];
    machineRoles: string[];
    tenants: TenantResource[];
    tagIndex: TagIndex;
    machineShellNames: string[];
    endpointRegistrations: EndpointRegistration[];
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
const Title = "Environments";
export async function environmentsPageLoader(repository: Repository, queryParams: EnvironmentsPageQueryParams): Promise<EnvironmentsPageLoaderData> {
    const tagIndex = await tenantTagsets.getTagIndex();
    const args = createEnvironmentSummaryArgs(queryParams, tagIndex);
    const environmentsSummaryPromise = repository.Environments.summary(args);
    const environmentsPromise = repository.Environments.all();
    const machineRolesPromise = repository.MachineRoles.all();
    const tenantsPromise = isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all() : Promise.resolve([]);
    const machineShellNamesPromise = repository.MachineShells.all();
    return {
        environmentsSummary: await environmentsSummaryPromise,
        environments: await environmentsPromise,
        machineRoles: await machineRolesPromise,
        tenants: await tenantsPromise,
        tagIndex,
        machineShellNames: await machineShellNamesPromise,
        endpointRegistrations: await endpointRegistry.getAllRegistrations(),
    };
}
export function EnvironmentsPage({ queryParams, setQueryParams, spaceId, loaderData }: EnvironmentsPageProps) {
    const dispatchAction = useAnalyticActionDispatch();
    const addEnvironmentDialogControls = useDialogTrigger();
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
    const { doBusyTask, status } = useLegacyDoBusyTask();
    const [debouncedPartialName, setDebouncedPartialName] = useDebouncedExternalState(queryParams.partialName, (value) => setQueryParams((prev) => ({ ...prev, partialName: value })), 200);
    const machineHealthStatuses = React.useMemo(() => MachineHealthStatusHelper.getMachineModelHealthStatusResources(), []);
    const navigate = useSpaceAwareNavigation();
    const { result: environmentsSummary, refetch: reloadEnvironmentSummaries } = useQuery((repository) => {
        const args = createEnvironmentSummaryArgs(queryParams, loaderData.tagIndex);
        return repository.Environments.summary(args);
    }, [queryParams, loaderData.tagIndex], "Load Environment Summaries", { initialResult: loaderData.environmentsSummary });
    useEffect(() => {
        setCurrentPageIndex(0);
    }, [environmentsSummary]);
    const addDeploymentTargetPageAction: PageAction = {
        type: "navigate",
        label: "Add Deployment Target",
        path: links.newDeploymentTargetPage.generateUrl({ spaceId }),
        buttonType: "secondary",
        hasPermissions: isAllowed({
            permission: Permission.MachineCreate,
            environment: "*",
            tenant: "*",
        }),
    };
    const addEnvironmentClicked = () => {
        dispatchAction("Add Environment", { resource: "Environment", action: AnalyticAction.Add });
        addEnvironmentDialogControls.openDialog();
    };
    const hasEnvironmentCreatePermission = isAllowed({
        permission: Permission.EnvironmentCreate,
        environment: "*",
    });
    const addEnvironmentPageAction: PrimaryPageAction = {
        type: "button",
        label: "Add Environment",
        hasPermissions: hasEnvironmentCreatePermission,
        onClick: addEnvironmentClicked,
    };
    const legacyOverflowMenuReorderItem = OverflowMenuItems.dialogItem("Reorder", <EnvironmentsSorter saveDone={reloadEnvironmentSummaries}/>, { permission: Permission.EnvironmentEdit, environment: "*" });
    const overflowMenu = OverflowMenuConverterVNext.convertAll([legacyOverflowMenuReorderItem]);
    const environmentSummaries = environmentsSummary.EnvironmentSummaries;
    const hasAvailableEnvironments = loaderData.environments && loaderData.environments.length > 0;
    const start = currentPageIndex * pageSize;
    const end = start + pageSize;
    const currentPageEnvironmentSummaries = environmentSummaries.slice(start, end);
    const pagination = (<Pagination currentPage={currentPageIndex + 1} itemsPerPageOptions={pageSizeOptions} label="Environments" onPageChange={(page) => setCurrentPageIndex(page - 1)} selectedItemsPerPage={pageSize} onPageSizeChange={(newPageSize) => setPageSize(newPageSize)} totalResults={environmentsSummary.EnvironmentSummaries.length}/>);
    const environmentExpanders = currentPageEnvironmentSummaries.map((environmentsSummary) => (<React.Fragment key={environmentsSummary.Environment.Id}>
            <Divider />
            <EnvironmentSummarySection endpointRegistrations={loaderData.endpointRegistrations} environmentSummary={environmentsSummary} filter={queryParams} isFiltering={isFiltering(queryParams)} tenants={loaderData.tenants} tagIndex={loaderData.tagIndex}/>
        </React.Fragment>));
    const advancedFilters = (<AdvancedFilters>
            <AdvancedFilterCheckbox label="Disabled only" value={queryParams.isDisabled === true} onChange={(x) => setQueryParams((prev) => ({ ...prev, isDisabled: x ? x : undefined }))}/>
            <AdvancedFilterCheckbox label="Hide empty environments" value={queryParams.hideEmptyEnvironments} onChange={(x) => setQueryParams((prev) => ({ ...prev, hideEmptyEnvironments: x }))}/>
            <AdvancedFilterTextInput fieldName={"deployment target"} value={queryParams.machinePartialName || ""} onChange={(x) => setQueryParams((prev) => ({ ...prev, machinePartialName: x }))}/>
            <EnvironmentMultiSelect environments={loaderData.environments} value={queryParams.environmentIds} onChange={(x) => setQueryParams((prev) => ({ ...prev, environmentIds: x }))}/>
            <TargetTagMultiSelect items={loaderData.machineRoles ? loaderData.machineRoles : []} value={queryParams.roles} onChange={(x) => setQueryParams((prev) => ({ ...prev, roles: x }))}/>
            <MachineModelHealthStatusMultiSelect items={machineHealthStatuses} value={queryParams.healthStatuses} onChange={(x) => setQueryParams((prev) => ({ ...prev, healthStatuses: x }))}/>
            <DeploymentTargetTypeMultiSelect key="filterDeploymentTargetType" items={loaderData.endpointRegistrations ?? []} value={queryParams.deploymentTargetTypes} onChange={(x) => setQueryParams((prev) => ({ ...prev, deploymentTargetTypes: x }))}/>
            <FeatureToggle feature={Feature.MultiTenancy}>
                <PermissionCheck permission={Permission.TenantView} tenant="*">
                    <TenantMultiSelect value={queryParams.tenantIds} items={loaderData.tenants} onChange={(x) => setQueryParams((prev) => ({ ...prev, tenantIds: x }))}/>
                    <TenantTagMultiSelect value={queryParams.tenantTags} doBusyTask={doBusyTask} onChange={(x) => setQueryParams((prev) => ({ ...prev, tenantTags: x }))}/>
                </PermissionCheck>
            </FeatureToggle>
            <ShellNameMultiSelect items={loaderData.machineShellNames ? loaderData.machineShellNames : []} value={queryParams.shellNames} onChange={(x) => setQueryParams((prev) => ({ ...prev, shellNames: x }))}/>
        </AdvancedFilters>);
    const tentacleUpgradesRequiredWarning = environmentSummaries.some((x) => x.TentacleUpgradesRequired) && <ConfirmTentacleUpgradePanel doBusyTask={doBusyTask} onTentacleUpgradeComplete={() => navigate.navigate(links.tasksPage.generateUrl())}/>;
    const filters = {
        inputs: [<SearchInput placeholder={"Search environments..."} accessibleName={"Search by environment name"} value={debouncedPartialName ?? ""} onChange={setDebouncedPartialName} autoFocus={true}/>],
        advancedFilters: {
            content: advancedFilters,
            onResetFilter: () => {
                setQueryParams(defaultEnvironmentSummaryFilter);
            },
            hasUserSelectedValues: isFiltering(queryParams),
        },
    };
    const dialogs = (<>
            <Dialog open={addEnvironmentDialogControls.isOpen}>
                <AddEnvironmentsDialog saveDone={async (env) => {
            addEnvironmentDialogControls.closeDialog();
            env.Type === "Parent"
                ? navigate.navigate(links.infrastructureParentEnvironmentPage.generateUrl({ spaceId, environmentId: env.Id }))
                : navigate.navigate(links.infrastructureEnvironmentPage.generateUrl({ spaceId, environmentId: env.Id }));
        }}/>
            </Dialog>
        </>);
    if (!hasAvailableEnvironments) {
        return (<>
                <PortalEmptyStateOnboarding title={"Add environments to group the deployment targets you will be deploying to"} description={"Environments group deployment targets so your software can be promoted through your deployment lifecycle. For instance, from Development to Test and finally into Production."} learnMore={<>
                            <ExternalLink href="OnboardingEnvironmentsLearnMore">Learn more</ExternalLink>
                            <ExternalVideoLink href="OnboardingEnvironmentsVideo" hideVideoIcon={true}>
                                Video (2 mins)
                            </ExternalVideoLink>
                        </>} primaryAction={addEnvironmentPageAction}/>
                {dialogs}
            </>);
    }
    return (<PageContent legacyStatus={status} header={{
            title: Title,
            primaryAction: addEnvironmentPageAction,
            pageActions: [addDeploymentTargetPageAction],
            overflowActions: overflowMenu.menuItems,
        }} filters={filters} pagination={{
            ui: pagination,
            placement: currentPageEnvironmentSummaries.length > 20 ? "topAndBottom" : "top",
        }} callout={{ type: "custom", content: <>{tentacleUpgradesRequiredWarning}</> }}>
            <BorderedContent>
                {overflowMenu.dialogs}
                {dialogs}
                {environmentExpanders.length > 0 && (<div className={expansionButtonsContainerStyles}>
                        <ExpansionButtons />
                    </div>)}
                {environmentExpanders.length > 0 ? environmentExpanders : <EmptyStateNoResults />}
            </BorderedContent>
        </PageContent>);
}
function isFiltering(queryParams: EnvironmentsPageQueryParams): boolean {
    return !isEqual(queryParams, defaultEnvironmentSummaryFilter);
}
const expansionButtonsContainerStyles = css({
    paddingTop: space[16],
    paddingBottom: space[4],
    paddingLeft: space[16],
});
